import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { SettingsSliceState, KeyBindings } from '../types';
import { DEFAULT_KEY_BINDINGS } from '../types';

/**
 * Redux slice for settings state management
 */

const initialState: SettingsSliceState = {
  // Audio
  masterVolume: 80,
  musicVolume: 70,
  sfxVolume: 85,

  // Graphics
  fullscreen: false,
  vsync: true,
  showFPS: false,
  postProcessing: true,
  bloom: true,
  screenShake: true,

  // Controls
  keyBindings: DEFAULT_KEY_BINDINGS,
  mouseSensitivity: 50,

  // Gameplay
  enableTutorials: true,
  showDamageNumbers: true,
  autoSave: true,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Audio settings
    setMasterVolume: (state, action: PayloadAction<number>) => {
      state.masterVolume = Math.max(0, Math.min(100, action.payload));
    },

    setMusicVolume: (state, action: PayloadAction<number>) => {
      state.musicVolume = Math.max(0, Math.min(100, action.payload));
    },

    setSFXVolume: (state, action: PayloadAction<number>) => {
      state.sfxVolume = Math.max(0, Math.min(100, action.payload));
    },

    // Graphics settings
    setFullscreen: (state, action: PayloadAction<boolean>) => {
      state.fullscreen = action.payload;
    },

    toggleFullscreen: (state) => {
      state.fullscreen = !state.fullscreen;
    },

    setVSync: (state, action: PayloadAction<boolean>) => {
      state.vsync = action.payload;
    },

    toggleVSync: (state) => {
      state.vsync = !state.vsync;
    },

    setShowFPS: (state, action: PayloadAction<boolean>) => {
      state.showFPS = action.payload;
    },

    toggleShowFPS: (state) => {
      state.showFPS = !state.showFPS;
    },

    setPostProcessing: (state, action: PayloadAction<boolean>) => {
      state.postProcessing = action.payload;
    },

    togglePostProcessing: (state) => {
      state.postProcessing = !state.postProcessing;
    },

    setBloom: (state, action: PayloadAction<boolean>) => {
      state.bloom = action.payload;
    },

    toggleBloom: (state) => {
      state.bloom = !state.bloom;
    },

    setScreenShake: (state, action: PayloadAction<boolean>) => {
      state.screenShake = action.payload;
    },

    toggleScreenShake: (state) => {
      state.screenShake = !state.screenShake;
    },

    // Controls
    setKeyBinding: (state, action: PayloadAction<{ action: keyof KeyBindings; key: string }>) => {
      state.keyBindings[action.payload.action] = action.payload.key;
    },

    setKeyBindings: (state, action: PayloadAction<KeyBindings>) => {
      state.keyBindings = action.payload;
    },

    resetKeyBindings: (state) => {
      state.keyBindings = DEFAULT_KEY_BINDINGS;
    },

    setMouseSensitivity: (state, action: PayloadAction<number>) => {
      state.mouseSensitivity = Math.max(0, Math.min(100, action.payload));
    },

    // Gameplay
    setEnableTutorials: (state, action: PayloadAction<boolean>) => {
      state.enableTutorials = action.payload;
    },

    toggleEnableTutorials: (state) => {
      state.enableTutorials = !state.enableTutorials;
    },

    setShowDamageNumbers: (state, action: PayloadAction<boolean>) => {
      state.showDamageNumbers = action.payload;
    },

    toggleShowDamageNumbers: (state) => {
      state.showDamageNumbers = !state.showDamageNumbers;
    },

    setAutoSave: (state, action: PayloadAction<boolean>) => {
      state.autoSave = action.payload;
    },

    toggleAutoSave: (state) => {
      state.autoSave = !state.autoSave;
    },

    // Load all settings from save
    loadSettings: (state, action: PayloadAction<Partial<SettingsSliceState>>) => {
      return { ...state, ...action.payload };
    },

    // Reset to defaults
    resetSettings: () => initialState,
  },
});

export const {
  setMasterVolume,
  setMusicVolume,
  setSFXVolume,
  setFullscreen,
  toggleFullscreen,
  setVSync,
  toggleVSync,
  setShowFPS,
  toggleShowFPS,
  setPostProcessing,
  togglePostProcessing,
  setBloom,
  toggleBloom,
  setScreenShake,
  toggleScreenShake,
  setKeyBinding,
  setKeyBindings,
  resetKeyBindings,
  setMouseSensitivity,
  setEnableTutorials,
  toggleEnableTutorials,
  setShowDamageNumbers,
  toggleShowDamageNumbers,
  setAutoSave,
  toggleAutoSave,
  loadSettings,
  resetSettings,
} = settingsSlice.actions;

export default settingsSlice.reducer;
